/**
 * IPT-501: 퇴사자 라이선스 현황 정렬 유틸
 *
 * - 원본 배열을 mutate 하지 않고 새 배열 반환
 * - null/undefined 는 항상 마지막
 * - lastLoginTime 은 Date 비교, 그 외는 문자열 비교(ko locale)
 * - sortKey 가 null 이면 원본 순서를 유지한 새 배열 반환
 */

export type SortDirection = 'asc' | 'desc';

export type ExEmployeeSortKey =
  | 'name'
  | 'pcip'
  | 'verInfo'
  | 'lastLoginTime'
  | 'nacAthenticator';

export interface ExEmployeeRow {
  id?: number;
  name?: string | null;
  pcip?: string | null;
  verInfo?: string | null;
  lastLoginTime?: string | Date | null;
  nacAthenticator?: string | null;
  [key: string]: unknown;
}

const isNil = (v: unknown): boolean => v === null || v === undefined || v === '';

const compareValues = (
  a: unknown,
  b: unknown,
  key: ExEmployeeSortKey,
): number => {
  const aNil = isNil(a);
  const bNil = isNil(b);
  // null/undefined/'' 는 항상 끝으로 (정렬 방향 무관)
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;

  if (key === 'lastLoginTime') {
    const aT = new Date(a as string | Date).getTime();
    const bT = new Date(b as string | Date).getTime();
    return aT - bT;
  }

  return String(a).localeCompare(String(b), 'ko');
};

export function sortExEmployees<T extends ExEmployeeRow>(
  rows: T[],
  sortKey: ExEmployeeSortKey | null,
  direction: SortDirection,
): T[] {
  const copy = [...rows];
  if (!sortKey) return copy;

  copy.sort((x, y) => {
    const cmp = compareValues(x[sortKey], y[sortKey], sortKey);
    if (cmp === 0) return 0;
    // null 은 항상 끝(방향 무관) — 위에서 1/-1 반환
    const xNil = isNil(x[sortKey]);
    const yNil = isNil(y[sortKey]);
    if (xNil || yNil) return cmp;
    return direction === 'asc' ? cmp : -cmp;
  });
  return copy;
}
